<script lang="ts" setup>
import type Connector from '@/classes/charger_classes/connector'
import type EVSE from '@/classes/charger_classes/evse'
import type Trip from '@/classes/trip_classes/trip'
import type Vehicle from '@/classes/vehicle_classes/vehicle'
import { GettersTypes, type State } from '@/store/store_types'
import type { DirectusBookableChargingUnit } from '@/types/directus-types'
import { capitalizeFirstLetter } from '@/utils/stringUtils'
import { isNonEmpty } from '@/utils/typeHelpers'
import { computed } from 'vue'
import { useStore } from 'vuex'

const {
  device,
  bookableDeviceData = undefined,
  flat = false,
  color = 'pwt_snow',
} = defineProps<{
  device: EVSE
  bookableDeviceData?: DirectusBookableChargingUnit
  flat?: boolean
  color?: string
}>()

const store = useStore<State>()
const currentVehicle = computed(() => {
  return (
    (store.getters[GettersTypes.selectedTripData] as Trip | undefined)?.vehicle ??
    (store.getters[GettersTypes.selectedVehicleData] as Vehicle | undefined)
  )
})

const compatibleConnectors = computed(() => {
  const usedDisplayNames: string[] = []
  const returnArray: Connector[] = []
  if (currentVehicle.value) {
    for (const connector of device.connectors) {
      if (usedDisplayNames.includes(connector.displayName)) continue
      if (connector.isCompatible(currentVehicle.value) !== 'incompatible') {
        usedDisplayNames.push(connector.displayName)
        returnArray.push(connector)
      }
    }
  }
  return returnArray
})

const incompatibleConnectors = computed(() => {
  const usedDisplayNames: string[] = []
  const returnArray: Connector[] = []
  if (currentVehicle.value) {
    for (const connector of device.connectors) {
      if (usedDisplayNames.includes(connector.displayName)) continue
      if (connector.isCompatible(currentVehicle.value) === 'incompatible') {
        usedDisplayNames.push(connector.displayName)
        returnArray.push(connector)
      }
    }
  }
  // fall back if no vehicle is selected all connectors are incompatible
  else {
    for (const connector of device.connectors) {
      if (usedDisplayNames.includes(connector.displayName)) continue
      usedDisplayNames.push(connector.displayName)
      returnArray.push(connector)
    }
  }

  return returnArray
})

const displayStatus = computed(() => {
  if (device.status === 'UNKNOWN' || device.status === 'StatusEnum(0)') return 'Status Unknown'
  // cspell:disable-next-line
  if (device.status === 'OUTOFORDER') return 'Out of order'
  return capitalizeFirstLetter(device.status)
})

const displayName = computed(
  () =>
    bookableDeviceData?.display_name ??
    (isNonEmpty(compatibleConnectors.value) ? compatibleConnectors.value[0].displayName : undefined) ??
    (isNonEmpty(incompatibleConnectors.value) ? incompatibleConnectors.value[0].displayName : undefined) ??
    'Unknown device type',
)
</script>
<template>
  <v-card class="pa-4 d-flex align-center" rounded="lg" :flat="flat" :color="color">
    <div>
      <p class="text-caption">{{ displayStatus }}</p>
      <p class="text-body-1">
        {{ displayName }}
      </p>
    </div>
    <v-spacer></v-spacer>
    <div v-for="connector in compatibleConnectors" style="position: relative">
      <img :src="connector.getPlugIconSrc" style="width: 32px; height: 32px" class="mr-2" />
      <v-icon
        v-if="displayStatus === 'Available'"
        color="pwt_green"
        size="10"
        class="position-absolute bg-white"
        style="top: -2px; right: 8px; border: 2px solid white"
      >
        mdi-check-circle
      </v-icon>
      <v-icon
        v-else-if="displayStatus === 'Status Unknown'"
        color="grey"
        size="10"
        class="position-absolute bg-white"
        style="top: -2px; right: 8px; border: 2px solid white"
      >
        mdi-help-circle
      </v-icon>
      <v-icon
        v-else-if="displayStatus === 'Out of order'"
        color="pwt_red"
        size="10"
        class="position-absolute bg-white"
        style="top: -2px; right: 8px; border: 2px solid white"
      >
        mdi-alert-circle
      </v-icon>
    </div>
    <div v-for="connector in incompatibleConnectors" style="position: relative">
      <img
        :src="connector.getPlugIconSrc"
        style="width: 32px; height: 32px; filter: grayscale(100%)"
        class="mr-2"
      />
      <v-icon
        v-if="displayStatus === 'Available'"
        color="pwt_green"
        size="10"
        class="position-absolute bg-white"
        style="top: -2px; right: 8px; border: 2px solid white"
      >
        mdi-check-circle
      </v-icon>
      <v-icon
        v-else-if="displayStatus === 'Status Unknown'"
        color="grey"
        size="10"
        class="position-absolute bg-white"
        style="top: -2px; right: 8px; border: 2px solid white"
      >
        mdi-help-circle
      </v-icon>
      <v-icon
        v-else-if="displayStatus === 'Out of order'"
        color="pwt_red"
        size="10"
        class="position-absolute bg-white"
        style="top: -2px; right: 8px; border: 2px solid white"
      >
        mdi-alert-circle
      </v-icon>
    </div>
  </v-card>
</template>
